import { useState, useEffect, memo, useRef } from 'react';
import { KatButton, KatSpinner, KatAlert } from '@amzn/katal-react';
import { AssetStatus, PreviewAssets, PreviewState } from 'src/types';
import { generatePreview } from 'src/utils/preview/PreviewGenerator';
import styles from './PreviewSection.module.css';
import { PreviewConstants, StateConstants } from 'src/constants';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { AssetType, PresignedUrlAction } from '@amzn/fire-tv-campaign-automation-service';
import { getControlPlaneClient } from 'src/contexts/ClientContext';
import { logger } from 'src/logger';
import { ControlPlaneClientUtils } from 'src/utils/clients/controlPlane';

/**
 * PreviewSection component displays preview states of a campaign with controls
 * to switch between selected and unselected states.
 */
const PreviewSection = memo(() => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { assetStates } = useAssetContext();
  const controlPlaneClient = getControlPlaneClient();
  const [activePreviewState, setActivePreviewState] = useState<PreviewState>(
    PreviewConstants.PREVIEW_LABELS.SELECTED,
  );

  // Cache refs
  const presignedUrlsCache = useRef<PreviewAssets | null>(null);
  const previewCache = useRef<{
    previews: Record<PreviewState, string>;
  }>({
    previews: {
      [PreviewConstants.PREVIEW_LABELS.SELECTED]: StateConstants.EMPTY_STRING,
      [PreviewConstants.PREVIEW_LABELS.UNSELECTED]: StateConstants.EMPTY_STRING,
      [PreviewConstants.PREVIEW_LABELS.VIDEO]: StateConstants.EMPTY_STRING,
    },
  });

  const lastProcessedKeys = useRef({
    bgImageKey: StateConstants.EMPTY_STRING,
    logoKey: StateConstants.EMPTY_STRING,
  });

  const bgImageKey = assetStates[AssetType.BACKGROUND_IMAGE]?.key;
  const logoKey = assetStates[AssetType.LOGO]?.key;

  // Single effect to handle asset changes and preview generation
  useEffect(() => {
    if (!bgImageKey || !logoKey) {
      return;
    }

    const staticPreviewKeysChanged =
      lastProcessedKeys.current.bgImageKey !== bgImageKey ||
      lastProcessedKeys.current.logoKey !== logoKey;

    const fetchPreview = async () => {
      setIsLoading(true);
      try {
        // Generate a new preview if keys have changed
        if (staticPreviewKeysChanged) {
          const [{ presignedUrl: bgImageUrl }, { presignedUrl: logoUrl }] =
            await ControlPlaneClientUtils.getPresignedUrls(
              controlPlaneClient,
              PresignedUrlAction.GET,
              [bgImageKey, logoKey],
            );

          // Update presignedUrlsCache
          presignedUrlsCache.current = {
            bgImage: bgImageUrl,
            logo: logoUrl,
          };

          lastProcessedKeys.current = {
            bgImageKey,
            logoKey,
          };

          const [selectedPreview, unselectedPreview] = await Promise.all([
            generatePreview(presignedUrlsCache.current, PreviewConstants.PREVIEW_LABELS.SELECTED),
            generatePreview(presignedUrlsCache.current, PreviewConstants.PREVIEW_LABELS.UNSELECTED),
          ]);

          // Cache both previews
          previewCache.current.previews[PreviewConstants.PREVIEW_LABELS.SELECTED] = selectedPreview;
          previewCache.current.previews[PreviewConstants.PREVIEW_LABELS.UNSELECTED] =
            unselectedPreview;
        }
        if (previewCache.current.previews[activePreviewState]) {
          logger.info('Preview found in cache, using it');
          setPreviewUrl(previewCache.current.previews[activePreviewState]);
        }
      } catch (error) {
        logger.error(
          'Failed to generate preview',
          error instanceof Error ? error : new Error(String(error)),
        );
        setError('Failed to generate preview. Please try again.');
        previewCache.current.previews[PreviewConstants.PREVIEW_LABELS.SELECTED] =
          StateConstants.EMPTY_STRING;
        previewCache.current.previews[PreviewConstants.PREVIEW_LABELS.UNSELECTED] =
          StateConstants.EMPTY_STRING;
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreview();
  }, [bgImageKey, logoKey, activePreviewState]);

  // Get video asset state once
  const videoAsset = assetStates[AssetType.VIDEO_TRAILER];

  // Effect for handling video asset context change
  useEffect(() => {
    const generateVideoPreview = async () => {
      setIsLoading(true);
      try {
        if (!videoAsset?.key || videoAsset.status !== AssetStatus.COMPLETED) {
          previewCache.current.previews[PreviewConstants.PREVIEW_LABELS.VIDEO] =
            StateConstants.EMPTY_STRING;
          return;
        }
        const [{ presignedUrl }] = await ControlPlaneClientUtils.getPresignedUrls(
          controlPlaneClient,
          PresignedUrlAction.GET,
          [videoAsset.key],
        );
        previewCache.current.previews[PreviewConstants.PREVIEW_LABELS.VIDEO] = presignedUrl;
        setPreviewUrl(presignedUrl);
      } catch (error) {
        logger.error('Failed to fetch video URL', { error });
        setError('Failed to load video. Please try again.');
        previewCache.current.previews[PreviewConstants.PREVIEW_LABELS.VIDEO] =
          StateConstants.EMPTY_STRING;
      } finally {
        setIsLoading(false);
      }
    };

    generateVideoPreview();
  }, [videoAsset]);

  // Effect to handle preview state changes
  useEffect(() => {
    if (!previewCache.current) {
      return;
    }

    try {
      logger.debug('Setting preview URL from cache', {
        state: activePreviewState,
        hasCache: Boolean(previewCache.current.previews[activePreviewState]),
      });
      if (previewCache.current.previews[activePreviewState]) {
        setPreviewUrl(previewCache.current.previews[activePreviewState]);
      }
    } catch (error) {
      logger.error(
        'Failed to update preview state',
        error instanceof Error ? error : new Error(String(error)),
      );
      setError?.('Failed to update preview state. Please try again.');
    }
  }, [activePreviewState]);

  return (
    <section className={styles.container}>
      <div className={styles.content}>
        <div className={styles.controls}>
          {[
            PreviewConstants.PREVIEW_LABELS.SELECTED,
            PreviewConstants.PREVIEW_LABELS.UNSELECTED,
            PreviewConstants.PREVIEW_LABELS.VIDEO,
          ].map((state) => (
            <KatButton
              key={state}
              variant={activePreviewState === state ? 'primary' : 'secondary'}
              onClick={() => setActivePreviewState(state)}
            >
              {state}
            </KatButton>
          ))}
        </div>

        {isLoading && (
          <div>
            <KatSpinner
              size="large"
              data-testid="preview-loading-spinner"
              aria-label="Loading preview"
            />{' '}
            Loading preview...
          </div>
        )}
        {error && !isLoading && (
          <KatAlert variant="danger" description={error} data-testid="preview-error-alert" />
        )}

        {previewUrl && (
          <div className={styles.previewWrapper}>
            {activePreviewState === PreviewConstants.PREVIEW_LABELS.VIDEO ? (
              assetStates[AssetType.VIDEO_TRAILER]?.key ? (
                <video
                  src={previewUrl}
                  controls
                  className={styles.preview}
                  data-testid="preview-video"
                />
              ) : (
                <KatAlert
                  variant="info"
                  description={PreviewConstants.PREVIEW_ALT_TEXT[activePreviewState]}
                  data-testid="video-upload-prompt"
                />
              )
            ) : bgImageKey && logoKey ? (
              <img
                src={previewUrl}
                alt={PreviewConstants.PREVIEW_ALT_TEXT[activePreviewState]}
                className={styles.preview}
              />
            ) : (
              <KatAlert
                variant="info"
                description={PreviewConstants.PREVIEW_ALT_TEXT[activePreviewState]}
                data-testid="image-upload-prompt"
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
});

PreviewSection.displayName = PreviewConstants.COMPONENT_NAMES.PREVIEW_SECTION;

export { PreviewSection };
